import {
  Download,
  User,
  Calendar,
  CheckCircle2,
  XCircle,
  FileText,
  Image,
  Film,
  File,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatFileSize, STATUS_CONFIG } from '../evidence-review.data';
import type { EvidenceItem } from '@/api/instructor-evidence';

// Icono según el tipo de archivo. Componente estático (a nivel de módulo) para no
// crear componentes durante el render (react-hooks/static-components).
function FileTypeIcon({ fileType, className }: { fileType: string; className?: string }) {
  const Icon = fileType.startsWith('image/')
    ? Image
    : fileType.startsWith('video/')
      ? Film
      : fileType.includes('pdf') || fileType.includes('document')
        ? FileText
        : File;
  return <Icon className={className} />;
}

// Tarjeta de una evidencia subida, con acciones de revisión. Extraído por ENG-LOC-01.
export function EvidenceCard({
  ev,
  onReview,
}: {
  ev: EvidenceItem;
  onReview: (action: 'approved' | 'rejected') => void;
}) {
  const statusCfg = STATUS_CONFIG[ev.status];
  const StatusIcon = statusCfg.icon;

  return (
    <div className="rounded-xl border border-border bg-card p-4 space-y-3">
      {/* File info */}
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <FileTypeIcon fileType={ev.file_type} className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-card-foreground">{ev.file_name}</p>
          <p className="text-xs text-muted-foreground">
            {formatFileSize(ev.file_size)} — {ev.file_type.split('/').pop()}
          </p>
        </div>
      </div>

      {/* Status badge */}
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ring-1',
            statusCfg.className,
          )}
        >
          <StatusIcon className="h-3 w-3" />
          {statusCfg.label}
        </span>
      </div>

      {/* User */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <User className="h-3.5 w-3.5" />
        <span>
          {ev.user.first_name} {ev.user.last_name}
        </span>
      </div>

      {/* Date */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Calendar className="h-3.5 w-3.5" />
        <span>{new Date(ev.uploaded_at).toLocaleDateString('es')}</span>
      </div>

      {/* Description */}
      {ev.description && (
        <p className="text-xs text-muted-foreground line-clamp-2">{ev.description}</p>
      )}

      {/* Reviewer info */}
      {ev.reviewer && ev.reviewed_at && (
        <p className="text-[10px] text-muted-foreground">
          Revisado por {ev.reviewer.first_name} {ev.reviewer.last_name} el{' '}
          {new Date(ev.reviewed_at).toLocaleDateString('es')}
        </p>
      )}

      {/* Review notes */}
      {ev.review_notes && (
        <p className="text-xs italic text-muted-foreground line-clamp-2">
          &ldquo;{ev.review_notes}&rdquo;
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1">
        <a
          href={ev.file_url}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium',
            'bg-primary text-primary-foreground hover:bg-primary/90 transition-colors',
          )}
        >
          <Download className="h-3.5 w-3.5" />
          Descargar
        </a>

        {ev.status === 'pending' && (
          <>
            <button
              onClick={() => onReview('approved')}
              className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-green-700"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Aprobar
            </button>
            <button
              onClick={() => onReview('rejected')}
              className="inline-flex items-center gap-1 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700"
            >
              <XCircle className="h-3.5 w-3.5" />
              Rechazar
            </button>
          </>
        )}
      </div>
    </div>
  );
}
